import React, { useState, useEffect } from 'react';
import './App.css';
import PlayersPanel from './components/PlayersPanel';
import TonightPanel from './components/TonightPanel';
import { getPlayers, getTonightSession } from './services/api';

function App() {
  const [players, setPlayers] = useState([]);
  const [tonightPlayerIds, setTonightPlayerIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load initial data
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Try to load from localStorage first (offline support)
      // Use cachedPlayers and cachedTonightIds as specified
      const cachedPlayers = localStorage.getItem('cachedPlayers');
      const cachedTonightIds = localStorage.getItem('cachedTonightIds');
      
      if (cachedPlayers) {
        try {
          setPlayers(JSON.parse(cachedPlayers));
        } catch (e) {
          console.warn('Failed to parse cached players:', e);
        }
      }
      if (cachedTonightIds) {
        try {
          setTonightPlayerIds(JSON.parse(cachedTonightIds));
        } catch (e) {
          console.warn('Failed to parse cached tonight IDs:', e);
        }
      }
      
      // Then try to fetch from backend
      try {
        const [playersData, sessionData] = await Promise.all([
          getPlayers(),
          getTonightSession()
        ]);
        
        setPlayers(playersData);
        setTonightPlayerIds(sessionData.tonightPlayerIds || []);
        
        // Update cache with successful responses
        localStorage.setItem('cachedPlayers', JSON.stringify(playersData));
        localStorage.setItem('cachedTonightIds', JSON.stringify(sessionData.tonightPlayerIds || []));
      } catch (apiError) {
        console.warn('Backend unavailable, using cached data:', apiError);
        // Continue with cached data if backend is down
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Refetch from backend after any player operation
  const handlePlayersUpdate = async () => {
    try {
      // Fetch fresh players list
      const playersData = await getPlayers();
      setPlayers(playersData);
      // Update cache
      localStorage.setItem('cachedPlayers', JSON.stringify(playersData));
    } catch (err) {
      console.warn('Failed to refetch players, using cached data:', err);
      // On error, try to use cached data
      const cachedPlayers = localStorage.getItem('cachedPlayers');
      if (cachedPlayers) {
        try {
          setPlayers(JSON.parse(cachedPlayers));
        } catch (e) {
          console.warn('Failed to parse cached players:', e);
        }
      }
    }
  };

  const handleTonightSessionUpdated = async () => {
    // Refetch session from backend
    try {
      const sessionData = await getTonightSession();
      const tonightIds = sessionData.tonightPlayerIds || [];
      setTonightPlayerIds(tonightIds);
      // Update cache with successful response
      localStorage.setItem('cachedTonightIds', JSON.stringify(tonightIds));
    } catch (err) {
      console.warn('Failed to refetch session, using cached data:', err);
      // On error, try to use cached data
      const cachedTonightIds = localStorage.getItem('cachedTonightIds');
      if (cachedTonightIds) {
        try {
          setTonightPlayerIds(JSON.parse(cachedTonightIds));
        } catch (e) {
          console.warn('Failed to parse cached tonight IDs:', e);
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="app">
        <div className="container">
          <div className="loading">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <div className="container">
        <header className="app-header">
          <h1>The Oche</h1>
        </header>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="two-box-layout">
          <TonightPanel
            players={players}
            tonightPlayerIds={tonightPlayerIds}
            onSessionUpdate={handleTonightSessionUpdated}
          />
          
          <PlayersPanel
            players={players}
            tonightPlayerIds={tonightPlayerIds}
            onPlayersUpdate={handlePlayersUpdate}
            onSessionUpdate={handleTonightSessionUpdated}
          />
        </div>
      </div>
    </div>
  );
}

export default App;
